/* =========================================================================
   My Surveying Direct, _shared.js
   Nav + mega panels, theme toggle, forms, comparison logic, scroll reveal.
   No external dependencies. Progressive enhancement: the site works without JS.
   ========================================================================= */
(function () {
  "use strict";

  /* ---------- Theme toggle -------------------------------------------------
     Dark is the default. Respect a stored choice, else the system preference. */
  var STORE = "mysd-theme";
  var root = document.documentElement;

  // Addendum 02: light is the default surface, dark is the opt-in.
  function applyTheme(t) {
    if (t === "dark") root.setAttribute("data-theme", "dark");
    else root.removeAttribute("data-theme");
  }
  try {
    var stored = localStorage.getItem(STORE);
    if (stored) applyTheme(stored);
    else if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      applyTheme("dark"); // honour an explicit system dark preference
    }
  } catch (e) {}

  function toggleTheme() {
    var next = root.getAttribute("data-theme") === "dark" ? "light" : "dark";
    applyTheme(next);
    try { localStorage.setItem(STORE, next); } catch (e) {}
  }

  /* ---------- Init on DOM ready ------------------------------------------- */
  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(function () {
    /* Theme buttons (any [data-theme-toggle]) */
    document.querySelectorAll("[data-theme-toggle]").forEach(function (b) {
      b.addEventListener("click", toggleTheme);
    });

    /* ---------- Mega panels (hover on desktop, click for keyboard) -------- */
    var navItems = document.querySelectorAll(".nav-main > li");
    navItems.forEach(function (li) {
      var trigger = li.querySelector("button, a");
      var hasMega = li.querySelector(".mega");
      if (!hasMega || !trigger) return;

      function open() { closeAll(); li.classList.add("open"); if (trigger.setAttribute) trigger.setAttribute("aria-expanded", "true"); }
      function close() { li.classList.remove("open"); if (trigger.setAttribute) trigger.setAttribute("aria-expanded", "false"); }

      li.addEventListener("mouseenter", open);
      li.addEventListener("mouseleave", function () {
        // A panel carrying a form stays open while anything inside it has focus.
        if (li.contains(document.activeElement) && li.querySelector("form")) return;
        close();
      });
      li.addEventListener("focusin", open);
      trigger.addEventListener("click", function (e) {
        e.preventDefault();
        if (li.classList.contains("open")) close(); else open();
      });
      li.addEventListener("keydown", function (e) { if (e.key === "Escape") { close(); trigger.focus(); } });
    });
    function closeAll() { navItems.forEach(function (li) { li.classList.remove("open"); }); }
    document.addEventListener("click", function (e) {
      if (!e.target.closest(".nav-main")) closeAll();
    });
    // Clicking inside a panel (a form field, say) must not toggle it shut.
    document.querySelectorAll(".nav-main .mega").forEach(function (m) {
      m.addEventListener("click", function (e) { e.stopPropagation(); });
    });

    /* ---------- Mobile nav ------------------------------------------------- */
    var navToggle = document.querySelector(".nav-toggle");
    var mobileNav = document.querySelector(".mobile-nav");
    if (navToggle && mobileNav) {
      navToggle.addEventListener("click", function () {
        var open = mobileNav.hasAttribute("hidden") ? false : true;
        if (open) { mobileNav.setAttribute("hidden", ""); navToggle.setAttribute("aria-expanded", "false"); }
        else { mobileNav.removeAttribute("hidden"); navToggle.setAttribute("aria-expanded", "true"); }
      });
    }

    /* ---------- Product buy-path selector ---------------------------------- */
    document.querySelectorAll(".paths").forEach(function (group) {
      var paths = group.querySelectorAll(".path");
      paths.forEach(function (p) {
        p.addEventListener("click", function () {
          paths.forEach(function (x) { x.classList.remove("sel"); x.setAttribute("aria-pressed", "false"); });
          p.classList.add("sel"); p.setAttribute("aria-pressed", "true");
          var target = p.getAttribute("data-reveal");
          if (target) {
            group.parentElement.querySelectorAll("[data-path-block]").forEach(function (b) {
              b.hidden = b.getAttribute("data-path-block") !== target;
            });
          }
        });
      });
    });

    /* ---------- Forms: validation + stub submit ---------------------------- */
    document.querySelectorAll("form[data-form]").forEach(function (form) {
      var success = form.parentElement.querySelector(".form-success") || document.getElementById(form.getAttribute("data-success"));
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var ok = true;
        form.querySelectorAll("[required]").forEach(function (input) {
          var field = input.closest(".field");
          var valid = input.value.trim() !== "" && !(input.type === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(input.value));
          if (field) field.classList.toggle("invalid", !valid);
          if (!valid && ok) { ok = false; input.focus(); }
        });
        if (!ok) return;

        var btn = form.querySelector("[type=submit]");
        if (btn) btn.setAttribute("aria-busy", "true");

        var payload = {};
        new FormData(form).forEach(function (v, k) { payload[k] = v; });

        var endpoint = form.getAttribute("action");
        function done() {
          if (btn) btn.removeAttribute("aria-busy");
          form.hidden = true;
          if (success) { success.classList.add("show"); success.setAttribute("role", "status"); success.scrollIntoView({ block: "center", behavior: "smooth" }); }
        }

        // Until a real endpoint is wired (see OPEN-ITEMS), log the payload and show success.
        if (!endpoint || endpoint === "#" || endpoint.indexOf("STUB") !== -1) {
          console.log("[MYSD form stub] " + (form.getAttribute("data-form") || "form"), payload);
          setTimeout(done, 550);
        } else {
          fetch(endpoint, { method: "POST", headers: { "Accept": "application/json", "Content-Type": "application/json" }, body: JSON.stringify(payload) })
            .then(done)
            .catch(function () { console.log("[MYSD form fallback] payload", payload); done(); });
        }
      });
      // Clear invalid state on input
      form.querySelectorAll(".input").forEach(function (input) {
        input.addEventListener("input", function () {
          var field = input.closest(".field");
          if (field) field.classList.remove("invalid");
        });
      });
    });

    /* ---------- Comparison engine (compare page) --------------------------- */
    initCompare();

    /* ---------- Scroll reveal (IntersectionObserver, staggered) ------------ */
    var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var revealEls = document.querySelectorAll(".reveal");
    if (reduce || !("IntersectionObserver" in window)) {
      revealEls.forEach(function (el) { el.classList.add("in"); });
    } else {
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var el = entry.target;
            var siblings = Array.prototype.slice.call(el.parentElement.querySelectorAll(":scope > .reveal"));
            var idx = Math.max(0, siblings.indexOf(el));
            el.style.transitionDelay = Math.min(idx * 100, 400) + "ms";
            el.classList.add("in");
            obs.unobserve(el);
          }
        });
      }, { rootMargin: "0px 0px -8% 0px", threshold: 0.05 });
      revealEls.forEach(function (el) { io.observe(el); });
    }
  });

  /* ---------- Comparison engine ------------------------------------------- */
  function initCompare() {
    var root = document.querySelector("[data-compare]");
    if (!root) return;
    var data = window.MYSD_COMPARE;
    if (!data) return;

    var picks = root.querySelectorAll("[data-compare-pick]");
    var out = root.querySelector("[data-compare-table]");
    var MAX = 4;

    function selected() {
      return Array.prototype.slice.call(picks)
        .filter(function (p) { return p.checked; })
        .map(function (p) { return p.value; });
    }

    function render() {
      var sel = selected();
      picks.forEach(function (p) {
        p.disabled = !p.checked && sel.length >= MAX;
        var chip = p.closest(".cmp-chip");
        if (chip) chip.classList.toggle("on", p.checked);
      });
      if (!out) return;
      if (sel.length < 2) {
        out.innerHTML = '<p class="muted" style="padding:24px 0">Select at least two instruments to compare them side by side.</p>';
        return;
      }
      var models = sel.map(function (id) { return data.models[id]; }).filter(Boolean);
      var rows = data.rows;
      var html = '<div class="table-scroll"><table class="spec-table"><thead><tr><th>Specification</th>';
      models.forEach(function (m) { html += "<th>" + esc(m.name) + "</th>"; });
      html += "</tr></thead><tbody>";
      rows.forEach(function (row) {
        html += "<tr><th>" + esc(row.label) + "</th>";
        models.forEach(function (m) {
          var v = m.specs[row.key];
          var cls = "";
          if (v === "Yes") cls = "yes";
          else if (v === "No") cls = "no";
          else if (v === "Not published") cls = "np";
          html += '<td class="' + cls + '">' + esc(v == null ? "Not published" : v) + "</td>";
        });
        html += "</tr>";
      });
      html += "</tbody></table></div>";
      out.innerHTML = html;
    }

    picks.forEach(function (p) { p.addEventListener("change", render); });
    render();
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
})();

/* =========================================================================
   Hero carousel. Auto-advance, arrows, dots, keyboard, pause on hover and
   on tab-hide. Only the visible slide's video plays, so three background
   videos never decode at once.
   ========================================================================= */
(function () {
  var root = document.querySelector(".hcar");
  if (!root) return;
  var slides = [].slice.call(root.querySelectorAll(".hslide"));
  var dots = [].slice.call(root.querySelectorAll(".hdots button"));
  var count = root.querySelector(".hcount b");
  var prev = root.querySelector("[data-hprev]");
  var next = root.querySelector("[data-hnext]");
  if (slides.length < 2) return;

  var i = 0, timer = null, DWELL = 7000, primed = false;
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* Slides after the first carry preload="none" and hold their URL in
     data-src, so the browser fetches nothing until the slide is reached.
     Before the fix all three videos downloaded at first paint: 8.4MB of an
     9.6MB page, when only slide 1 is ever seen above the fold. Attach the
     real source on first activation, then let it behave normally. */
  function hydrate(v) {
    if (!v || v.dataset.hydrated) return;
    if (v.dataset.poster) {
      v.setAttribute("poster", v.dataset.poster);
      delete v.dataset.poster;
    }
    var src = v.querySelector("source[data-src]");
    if (src) {
      src.setAttribute("src", src.getAttribute("data-src"));
      src.removeAttribute("data-src");
      v.dataset.hydrated = "1";
      v.load();
    }
  }

  function show(n) {
    i = (n + slides.length) % slides.length;
    slides.forEach(function (s, k) {
      var on = k === i;
      s.classList.toggle("on", on);
      var v = s.querySelector("video");
      if (v) {
        if (on) { if (primed) activate(v); }
        else { v.pause(); try { v.currentTime = 0; } catch (e) {} }
      }
    });
    // Warm the next slide once the current one is settled, so an advance is
    // never a cold start. Idle time only, never competing with first paint.
    if (primed) {
      var nxt = slides[(i + 1) % slides.length];
      if (nxt && window.requestIdleCallback) {
        requestIdleCallback(function () { hydrate(nxt.querySelector("video")); }, { timeout: 3000 });
      }
    }
    dots.forEach(function (d, k) { d.setAttribute("aria-selected", k === i ? "true" : "false"); });
    if (count) count.textContent = ("0" + (i + 1)).slice(-2);
  }
  function start() { if (reduce) return; stop(); timer = setInterval(function () { show(i + 1); }, DWELL); }
  function stop() { if (timer) { clearInterval(timer); timer = null; } }

  dots.forEach(function (d, k) { d.addEventListener("click", function () { show(k); start(); }); });
  if (prev) prev.addEventListener("click", function () { show(i - 1); start(); });
  if (next) next.addEventListener("click", function () { show(i + 1); start(); });
  root.addEventListener("mouseenter", stop);
  root.addEventListener("mouseleave", start);
  root.addEventListener("keydown", function (e) {
    if (e.key === "ArrowLeft") { show(i - 1); start(); }
    if (e.key === "ArrowRight") { show(i + 1); start(); }
  });
  document.addEventListener("visibilitychange", function () { document.hidden ? stop() : start(); });

  function activate(v) {
    if (!v) return;
    hydrate(v);
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }

  /* Nothing in the hero touches the network until the page has loaded and the
     main thread is idle.

     Slide 1 used to carry preload="auto", autoplay and a real src, so 1.8MB of
     video was fetched during parse and the <video> element itself became the
     largest contentful paint. Measured 31 Jul: LCP 7.8s on throttled mobile,
     of which 5.55s was Load Delay. That could never have been tuned faster,
     because the LCP candidate WAS the download. Now the 59KB poster paints and
     the video arrives behind it.

     Swapping slide 1 to data-src alone would not have worked: show(0) runs at
     init and would have hydrated it immediately. The primed flag is the fix. */
  function prime() {
    if (primed) return;
    primed = true;
    if (reduce) return;          // motion is off, the poster is the hero
    activate(slides[i].querySelector("video"));
  }
  function whenIdle(fn) {
    if (window.requestIdleCallback) requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 400);
  }

  show(0);
  if (document.readyState === "complete") whenIdle(prime);
  else window.addEventListener("load", function () { whenIdle(prime); });
  start();
})();
